import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Grid,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import EventIcon from '@mui/icons-material/Event';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useNavigate } from 'react-router-dom';

import api from '../api/axios';
import PageAnimation from '../components/animations/PageAnimation';
import SEO from '../components/SEO';
import { usePageLoading } from '../contexts/PageLoadingContext';


const GREEN = '#75a84b';
const currency = new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
});

const formatDate = (value) => {
    if (!value) return 'Not set';
    const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
    return Number.isNaN(date.getTime())
        ? String(value)
        : date.toLocaleDateString('en-CA', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
};

const statementStatus = (statement) => {
    if (statement.is_paid) {
        return { label: 'Paid', color: 'success' };
    }
    if (statement.is_overdue) {
        return { label: 'Overdue', color: 'error' };
    }
    if (statement.status === 'sent') {
        return { label: 'Sent', color: 'info' };
    }
    return { label: 'Available', color: 'warning' };
};


export default function StatementsPage() {
    const navigate = useNavigate();
    const { setPageLoading } = usePageLoading();
    const [statements, setStatements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchStatements = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await api.get('/statements');
            setStatements(response.data || []);
        } catch (err) {
            setError(
                err.response?.data?.msg
                || 'We could not load your statements. Please try again.'
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStatements();
    }, [fetchStatements]);

    useEffect(() => {
        setPageLoading(loading);
        return () => setPageLoading(false);
    }, [loading, setPageLoading]);

    const outstandingBalance = useMemo(
        () => statements
            .filter((statement) => !statement.is_paid)
            .reduce((sum, statement) => sum + Number(statement.total_amount || 0), 0),
        [statements]
    );

    const handleDownload = (statement) => {
        const token = localStorage.getItem('accessToken') || '';
        if (!token) {
            navigate('/login');
            return;
        }

        let baseURL = (
            api.defaults.baseURL
            || process.env.REACT_APP_API_URL
            || 'https://mataxexpress.ca/api'
        );
        if (baseURL.startsWith('/')) {
            baseURL = window.location.origin + baseURL;
        }

        const url = `${baseURL}/statements/${statement.id}/download?token=${encodeURIComponent(token)}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const renderContent = () => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                    <CircularProgress size={56} sx={{ color: GREEN }} />
                </Box>
            );
        }

        if (error) {
            return (
                <Alert
                    severity="error"
                    action={(
                        <Button color="inherit" size="small" onClick={fetchStatements}>
                            Retry
                        </Button>
                    )}
                    sx={{ mt: 3 }}
                >
                    {error}
                </Alert>
            );
        }

        if (statements.length === 0) {
            return (
                <Paper
                    elevation={0}
                    sx={{
                        mt: 3,
                        p: { xs: 4, md: 6 },
                        textAlign: 'center',
                        border: '1px solid #e8e8e8',
                        borderRadius: 3,
                        bgcolor: '#fcfdfa',
                    }}
                >
                    <DescriptionOutlinedIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700 }} gutterBottom>
                        No statements yet
                    </Typography>
                    <Typography color="text.secondary">
                        Generated account statements will appear here when they become available.
                    </Typography>
                </Paper>
            );
        }

        return (
            <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
                {statements.map((statement) => {
                    const status = statementStatus(statement);
                    return (
                        <Grid key={statement.id} size={{ xs: 12, md: 6 }}>
                            <Card
                                variant="outlined"
                                sx={{
                                    height: '100%',
                                    borderRadius: 3,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderColor: '#e4e8e0',
                                    transition: 'transform .2s, box-shadow .2s',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                                    },
                                }}
                            >
                                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                                    <Stack direction="row" alignItems="flex-start" spacing={2}>
                                        <Avatar sx={{ bgcolor: '#eef5e8', color: GREEN }}>
                                            <ReceiptLongIcon />
                                        </Avatar>
                                        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                            <Stack
                                                direction="row"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                spacing={1}
                                            >
                                                <Typography variant="h6" sx={{ fontWeight: 750 }}>
                                                    {statement.statement_no || 'Statement'}
                                                </Typography>
                                                <Chip
                                                    size="small"
                                                    label={status.label}
                                                    color={status.color}
                                                    sx={{ fontWeight: 700 }}
                                                />
                                            </Stack>
                                            <Typography variant="body2" color="text.secondary">
                                                Generated {formatDate(statement.statement_date)}
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    <Box
                                        sx={{
                                            mt: 3,
                                            p: 2,
                                            borderRadius: 2,
                                            bgcolor: '#f7f9f5',
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 1fr',
                                            gap: 2,
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Statement total
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                                {currency.format(Number(statement.total_amount || 0))}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Due date
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                                {formatDate(statement.due_date)}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={{ xs: 0.5, sm: 2 }}
                                        sx={{ mt: 2 }}
                                    >
                                        <Typography variant="body2" color="text.secondary">
                                            {statement.invoice_count} invoice{statement.invoice_count === 1 ? '' : 's'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {statement.statement_duration} cycle
                                        </Typography>
                                        {Number(statement.interest_amount || 0) > 0 && (
                                            <Typography variant="body2" color="error.main">
                                                Includes {currency.format(Number(statement.interest_amount))} interest
                                            </Typography>
                                        )}
                                    </Stack>
                                </CardContent>
                                <CardActions sx={{ px: 3, pb: 3, pt: 0 }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        startIcon={<DownloadIcon />}
                                        onClick={() => handleDownload(statement)}
                                        sx={{
                                            bgcolor: GREEN,
                                            py: 1.1,
                                            fontWeight: 700,
                                            '&:hover': { bgcolor: '#5c8d36' },
                                        }}
                                    >
                                        Download / Print Statement
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
        );
    };

    return (
        <PageAnimation>
            <SEO
                title="Account Statements | Matax Express"
                description="View and download your Matax Express account statements."
                url="https://mataxexpress.ca/statements"
                noindex
            />
            <Box
                sx={{
                    bgcolor: '#f5f9f1',
                    borderBottom: '1px solid #e4eadf',
                    py: { xs: 4, md: 5 },
                }}
            >
                <Container maxWidth="lg">
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={2}
                    >
                        <Box>
                            <Typography variant="h4" component="h1" sx={{ fontWeight: 800 }}>
                                Account Statements
                            </Typography>
                            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                                Review generated statements and save or print a copy.
                            </Typography>
                        </Box>
                        {!loading && !error && statements.length > 0 && (
                            <Paper
                                elevation={0}
                                sx={{
                                    px: 2.5,
                                    py: 1.5,
                                    borderRadius: 2.5,
                                    border: '1px solid #dce7d4',
                                    bgcolor: 'white',
                                }}
                            >
                                <Stack direction="row" spacing={1.25} alignItems="center">
                                    <EventIcon sx={{ color: GREEN }} />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Statement balance
                                        </Typography>
                                        <Typography sx={{ fontWeight: 800 }}>
                                            {currency.format(outstandingBalance)}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                        )}
                    </Stack>
                </Container>
            </Box>
            <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 }, minHeight: '45vh' }}>
                {renderContent()}
            </Container>
        </PageAnimation>
    );
}
